import { useCallback, useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import {
  clearPwaUpdateInProgress,
  markPwaUpdateInProgress,
  reloadAfterPwaUpdate,
} from "@app/helpers/pwaUpdate";

const updateCheckInterval = 15 * 60 * 1000;
const updateAcceptedKey = "pwa_update_accepted_until";
const updateAcceptedTtl = 30 * 60 * 1000;

export function useUpdatePwa() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [acceptedUpdateKey, setAcceptedUpdateKey] = useState(() =>
    getAcceptedUpdateKey(),
  );
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [swUrl, setSwUrl] = useState("");
  const [swVersionKey, setSwVersionKey] = useState("");
  const isPreviewForced =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("pwa-update-preview");

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      setSwUrl(swUrl);
      setRegistration(r ?? null);

      // Check for updates immediately when service worker is registered (when app renders)
      if (r?.active?.state === "activated") {
        checkForUpdate(swUrl, r, setSwVersionKey, setNeedRefresh);
      } else if (r?.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") {
            checkForUpdate(swUrl, r, setSwVersionKey, setNeedRefresh);
          }
        });
      }
    },
  });

  useEffect(() => {
    const timeout = window.setTimeout(clearPwaUpdateInProgress, 5000);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!registration || !swUrl) return undefined;

    const check = () =>
      checkForUpdate(swUrl, registration, setSwVersionKey, setNeedRefresh);
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const interval = window.setInterval(checkWhenVisible, updateCheckInterval);

    window.addEventListener("online", check);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", checkWhenVisible);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", check);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", checkWhenVisible);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [registration, setNeedRefresh, swUrl]);

  const currentUpdateKey = getCurrentUpdateKey(swUrl, swVersionKey);

  const refresh = useCallback(() => {
    if (!isUpdating) {
      let didReload = false;
      const reloadOnce = () => {
        if (didReload) return;

        didReload = true;
        window.clearTimeout(fallbackTimeout);
        reloadAfterPwaUpdate();
      };

      setIsUpdating(true);
      setNeedRefresh(false);
      setAcceptedUpdateKey(rememberAcceptedUpdate(currentUpdateKey));
      markPwaUpdateInProgress();

      const fallbackTimeout = window.setTimeout(reloadOnce, 4000);

      navigator.serviceWorker?.addEventListener(
        "controllerchange",
        reloadOnce,
        {
          once: true,
        },
      );

      void updateServiceWorker(true).catch(reloadOnce);
    }
  }, [currentUpdateKey, isUpdating, setNeedRefresh, updateServiceWorker]);

  const hasAcceptedUpdate =
    acceptedUpdateKey !== null && acceptedUpdateKey === currentUpdateKey;

  return {
    isUpdating,
    needRefresh: !hasAcceptedUpdate && (needRefresh || isPreviewForced),
    refresh,
  };
}

/**
 * Check for service worker updates immediately
 */
async function checkForUpdate(
  swUrl: string,
  r: ServiceWorkerRegistration,
  onVersionChange?: (versionKey: string) => void,
  onNeedRefresh?: (needRefresh: boolean) => void,
) {
  if ("onLine" in navigator && !navigator.onLine) return;

  try {
    const resp = await fetch(swUrl, {
      cache: "no-store",
      headers: {
        cache: "no-store",
        "cache-control": "no-cache",
      },
    });

    if (resp?.status === 200) {
      onVersionChange?.(await getServiceWorkerVersionKey(resp, swUrl));
      await r.update();

      if (r.waiting) {
        onNeedRefresh?.(true);
      }
    }
  } catch {
    // Update checks should never interrupt the active app session.
  }
}

function hasRecentlyAcceptedUpdate() {
  if (typeof window === "undefined") return false;

  const acceptedUpdate = readAcceptedUpdate();
  if (!acceptedUpdate) return false;

  const isRecent = Date.now() < acceptedUpdate.until;
  if (!isRecent) {
    window.localStorage.removeItem(updateAcceptedKey);
  }

  return isRecent;
}

function getCurrentUpdateKey(swUrl: string, swVersionKey: string) {
  return swVersionKey || swUrl || "default";
}

function getAcceptedUpdateKey() {
  if (!hasRecentlyAcceptedUpdate()) return null;

  return readAcceptedUpdate()?.key ?? null;
}

function rememberAcceptedUpdate(key: string) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      updateAcceptedKey,
      JSON.stringify({
        key,
        until: Date.now() + updateAcceptedTtl,
      }),
    );
  }

  return key;
}

async function getServiceWorkerVersionKey(response: Response, swUrl: string) {
  return `${swUrl}:${hashString(await response.text())}`;
}

function hashString(value: string) {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return hash.toString(36);
}

function readAcceptedUpdate() {
  if (typeof window === "undefined") return null;

  const rawValue = window.localStorage.getItem(updateAcceptedKey);
  if (!rawValue) return null;

  const legacyUntil = Number(rawValue);
  if (legacyUntil) {
    return { key: "default", until: legacyUntil };
  }

  try {
    const parsed = JSON.parse(rawValue) as { key?: string; until?: number };
    if (!parsed.key || typeof parsed.until !== "number") return null;

    return {
      key: parsed.key,
      until: parsed.until,
    };
  } catch {
    window.localStorage.removeItem(updateAcceptedKey);
    return null;
  }
}
